import React, { useMemo } from 'react';
import './css/AgePricingEditor.css';

const WEEKDAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const DAY_LABELS = {
  MONDAY: 'Mon',
  TUESDAY: 'Tue',
  WEDNESDAY: 'Wed',
  THURSDAY: 'Thu',
  FRIDAY: 'Fri',
  SATURDAY: 'Sat',
  SUNDAY: 'Sun',
};

const MODE_OPTIONS = [
  { value: 'REGULAR', label: 'Regular (ASAP)' },
  { value: 'FIXED', label: 'Fixed slots only' },
  { value: 'BOTH', label: 'Both regular + fixed' },
];

export function emptyDeliverySlots() {
  return WEEKDAYS.reduce((acc, day) => {
    acc[day] = [];
    return acc;
  }, {});
}

export function normalizeDeliverySlots(payload) {
  const base = emptyDeliverySlots();
  if (!payload || typeof payload !== 'object') return base;
  WEEKDAYS.forEach((day) => {
    const times = payload[day] ?? payload[day.toLowerCase()] ?? [];
    if (!Array.isArray(times)) {
      base[day] = [];
      return;
    }
    base[day] = [
      ...new Set(
        times
          .map((t) => String(t || '').slice(0, 5))
          .filter((t) => /^\d{2}:\d{2}$/.test(t)),
      ),
    ].sort();
  });
  return base;
}

/**
 * Weekday fixed delivery times (no cutoff) + delivery mode.
 * Optional party prep fields for restaurants.
 */
export default function DeliverySlotsEditor({
  deliveryMode = 'REGULAR',
  deliverySlots,
  onModeChange,
  onSlotsChange,
  disabled = false,
  showPartyPrep = false,
  partyPrepBaseMinutes = 45,
  partyPrepMinutesPerPack = 2,
  onPartyPrepChange,
  title = 'MYEATS delivery schedule',
  hint = 'Regular = ASAP ETA. Fixed = show next available slot (no order cutoff). Both = customer can use either.',
}) {
  const slots = useMemo(
    () => normalizeDeliverySlots(deliverySlots),
    [deliverySlots],
  );
  const showSlots = deliveryMode === 'FIXED' || deliveryMode === 'BOTH';

  const updateDayTimes = (day, timesText) => {
    const times = timesText
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => (t.length === 4 && t.includes(':') ? `0${t}` : t))
      .filter((t) => /^\d{1,2}:\d{2}$/.test(t))
      .map((t) => {
        const [h, m] = t.split(':');
        return `${String(h).padStart(2, '0')}:${m}`;
      });
    onSlotsChange?.({
      ...slots,
      [day]: [...new Set(times)].sort(),
    });
  };

  const addTime = (day, timeValue) => {
    const t = String(timeValue || '').slice(0, 5);
    if (!/^\d{2}:\d{2}$/.test(t)) return;
    const next = [...new Set([...(slots[day] || []), t])].sort();
    onSlotsChange?.({ ...slots, [day]: next });
  };

  const removeTime = (day, timeValue) => {
    onSlotsChange?.({
      ...slots,
      [day]: (slots[day] || []).filter((t) => t !== timeValue),
    });
  };

  return (
    <div className="age-pricing-editor meal-delivery-editor">
      <div className="age-pricing-editor__header">
        <label className="age-pricing-editor__title">{title}</label>
        <p className="age-pricing-editor__hint">{hint}</p>
      </div>

      <div className="age-pricing-editor__table-wrap" style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
          Delivery mode
        </label>
        <select
          value={deliveryMode || 'REGULAR'}
          disabled={disabled}
          onChange={(e) => onModeChange?.(e.target.value)}
          aria-label="Delivery mode"
        >
          {MODE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {showPartyPrep ? (
        <div
          className="age-pricing-editor__table-wrap"
          style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}
        >
          <label>
            Party prep base (min)
            <input
              type="number"
              min={0}
              disabled={disabled}
              value={partyPrepBaseMinutes}
              onChange={(e) =>
                onPartyPrepChange?.({
                  partyPrepBaseMinutes: e.target.value,
                  partyPrepMinutesPerPack,
                })
              }
              style={{ display: 'block', marginTop: 4 }}
            />
          </label>
          <label>
            Party prep per pack (min)
            <input
              type="number"
              min={0}
              disabled={disabled}
              value={partyPrepMinutesPerPack}
              onChange={(e) =>
                onPartyPrepChange?.({
                  partyPrepBaseMinutes,
                  partyPrepMinutesPerPack: e.target.value,
                })
              }
              style={{ display: 'block', marginTop: 4 }}
            />
          </label>
        </div>
      ) : null}

      {showSlots ? (
        <div className="age-pricing-editor__table-wrap">
          <table className="age-pricing-editor__table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Fixed delivery times (HH:mm)</th>
                <th>Add</th>
              </tr>
            </thead>
            <tbody>
              {WEEKDAYS.map((day) => (
                <tr key={day}>
                  <td>{DAY_LABELS[day]}</td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(slots[day] || []).length === 0 ? (
                        <span style={{ opacity: 0.6 }}>None</span>
                      ) : (
                        (slots[day] || []).map((t) => (
                          <button
                            key={t}
                            type="button"
                            disabled={disabled}
                            onClick={() => removeTime(day, t)}
                            title="Remove time"
                            style={{
                              border: '1px solid #ccc',
                              borderRadius: 4,
                              padding: '2px 8px',
                              background: '#fff',
                              cursor: disabled ? 'default' : 'pointer',
                            }}
                          >
                            {t} ×
                          </button>
                        ))
                      )}
                    </div>
                    <input
                      type="text"
                      disabled={disabled}
                      placeholder="Or paste 09:00, 13:00, 18:00"
                      defaultValue={(slots[day] || []).join(', ')}
                      key={`${day}-${(slots[day] || []).join(',')}`}
                      onBlur={(e) => updateDayTimes(day, e.target.value)}
                      style={{ marginTop: 6, width: '100%' }}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      disabled={disabled}
                      onChange={(e) => {
                        if (e.target.value) {
                          addTime(day, e.target.value);
                          e.target.value = '';
                        }
                      }}
                      aria-label={`Add time for ${day}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

export { WEEKDAYS, MODE_OPTIONS };
